import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from "@angular/core";

/**
 * Accessibility fix for PrimeNG toast status messages
 * (WCAG 4.1.3 Status Messages / 4.1.2 Name, Role, Value / 2.1.1 Keyboard).
 *
 * PrimeNG (v21) renders every toast message as a `.p-toast-message` element
 * with `role="alert"` and `aria-atomic="true"`, and toggles its visibility
 * through an enter animation (`pMotion`). Because `role="alert"` is an
 * assertive live region, screen readers announce the message BOTH when the
 * node is inserted into the DOM AND again when the enter animation makes it
 * visible, so a single status message is announced twice. Its close button
 * also carries `autofocus`, which would pair the announcement with a focus
 * shift into the toast.
 *
 * To guarantee a SINGLE announcement, for each rendered message this directive:
 *   1. silences PrimeNG's own live region by removing `role="alert"`,
 *      `aria-live` and `aria-atomic` from the `.p-toast-message`, so the visual
 *      toast no longer fires its own (duplicated) announcement;
 *   2. mirrors the message text ONCE into a single directive-owned,
 *      visually-hidden live region (`role="status"`/polite for success/info,
 *      `role="alert"`/assertive for warn/error) so the message is announced
 *      exactly once; the region is cleared when the toast is removed so an
 *      identical follow-up message is still announced as a fresh change;
 *   3. removes `autofocus` from the close button (and blurs it if it was
 *      auto-focused) so the announcement is not combined with a focus shift.
 *
 * Keyboard operability (WCAG 2.1.1): the native close `<button>` is already in
 * the tab sequence, but PrimeNG only pauses the auto-dismiss timer on
 * mouse enter/leave, so a keyboard user tabbing towards the button can miss it
 * before the toast disappears. We bridge keyboard focus to PrimeNG's own pause
 * logic by dispatching `mouseenter`/`mouseleave` on the message when focus
 * enters/leaves it. This keeps the toast alive while it holds keyboard focus
 * WITHOUT moving focus into it (so the single-announcement fix is preserved).
 */
@Directive({
  standalone: false,
  selector: "p-toast"
})
export class ToastSingleAnnounceDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;
  private liveRegion: HTMLElement | null = null;
  private readonly onFocusIn = (event: FocusEvent) => this.pauseDismissForFocus(event, "mouseenter");
  private readonly onFocusOut = (event: FocusEvent) => this.pauseDismissForFocus(event, "mouseleave");

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      // Pre-create the live region NOW (empty) so it is already present in the
      // DOM before any toast appears. Screen readers only announce content that
      // changes INSIDE a live region that already existed; a region inserted
      // together with its text in the same tick is commonly NOT announced
      // (NVDA/JAWS). Creating it eagerly guarantees the first toast is spoken.
      this.ensureLiveRegion();

      this.observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((node) => this.normalizeMessage(node));
          mutation.removedNodes.forEach((node) => this.clearAnnouncementFor(node));
        }
      });
      this.observer.observe(this.host.nativeElement, { childList: true, subtree: true });

      const host = this.host.nativeElement;
      host.addEventListener("focusin", this.onFocusIn);
      host.addEventListener("focusout", this.onFocusOut);
    });
  }

  /**
   * Reuse PrimeNG's mouse-based pause/resume so the auto-dismiss timer is held
   * while the toast has keyboard focus, letting keyboard users reach and
   * operate the close button.
   */
  private pauseDismissForFocus(event: FocusEvent, type: "mouseenter" | "mouseleave"): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const message = target.closest<HTMLElement>(".p-toast-message");
    if (!message) {
      return;
    }
    // On focusout, only resume when focus actually left this message.
    if (type === "mouseleave") {
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Node && message.contains(nextTarget)) {
        return;
      }
    }
    message.dispatchEvent(new MouseEvent(type, { bubbles: false }));
  }

  private normalizeMessage(node: Node): void {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const message = node.matches(".p-toast-message")
      ? node
      : node.querySelector<HTMLElement>(".p-toast-message");
    if (!message) {
      return;
    }

    // 1. Silence PrimeNG's own live region so the toast does not fire its own
    //    (duplicated) announcement. role="alert" is announced both on DOM
    //    insertion and again when the enter animation makes the toast visible.
    message.removeAttribute("aria-live");
    message.removeAttribute("aria-atomic");
    if (message.getAttribute("role") === "alert") {
      message.removeAttribute("role");
    }

    // 2. Announce the message text exactly once through a single owned region.
    this.announceOnce(message);

    // 3. Do not combine the announcement with a focus shift to the toast.
    const autofocusEl = message.querySelector<HTMLElement>("[autofocus]");
    if (autofocusEl) {
      autofocusEl.removeAttribute("autofocus");
      const activeEl = message.ownerDocument?.activeElement;
      if (activeEl && message.contains(activeEl)) {
        autofocusEl.blur();
      }
    }
  }

  /**
   * Mirror the toast text into a single, directive-owned visually-hidden live
   * region so the status message is announced exactly once. Warn/error toasts
   * use an assertive `role="alert"` region; everything else uses a polite
   * `role="status"` region (WCAG 4.1.3).
   */
  private announceOnce(message: HTMLElement): void {
    const summary = message.querySelector(".p-toast-summary")?.textContent?.trim() ?? "";
    const detail = message.querySelector(".p-toast-detail")?.textContent?.trim() ?? "";
    const text = [summary, detail].filter(Boolean).join(". ").trim() || (message.textContent?.trim() ?? "");
    if (!text) {
      return;
    }
    const assertive =
      message.classList.contains("p-toast-message-error") ||
      message.classList.contains("p-toast-message-warn");
    const region = this.ensureLiveRegion();
    region.setAttribute("role", assertive ? "alert" : "status");
    region.setAttribute("aria-live", assertive ? "assertive" : "polite");
    // Clear now, then set the text in a LATER tick. Clearing and setting in the
    // same synchronous tick collapses into a single "no change" for some screen
    // readers (and never re-announces an identical consecutive message). By
    // clearing first and deferring the set, the region goes empty → text as two
    // distinct mutations, which NVDA/JAWS reliably announce.
    region.textContent = "";
    setTimeout(() => {
      region.textContent = text;
    }, 50);
  }

  /**
   * Lazily create the single visually-hidden live region, appended to the host
   * `<p-toast>` (which persists after individual toasts are dismissed).
   */
  private ensureLiveRegion(): HTMLElement {
    if (this.liveRegion) {
      return this.liveRegion;
    }
    const doc = this.host.nativeElement.ownerDocument;
    const region = doc.createElement("div");
    region.setAttribute("aria-atomic", "true");
    region.setAttribute("aria-live", "polite");
    region.setAttribute("role", "status");
    region.className = "sr-only";
    region.style.cssText =
      "position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;";
    this.host.nativeElement.appendChild(region);
    this.liveRegion = region;
    return region;
  }

  /**
   * When a toast message is removed, clear the live region so a subsequent
   * identical message is announced as a fresh content change.
   */
  private clearAnnouncementFor(node: Node): void {
    if (!this.liveRegion || !(node instanceof HTMLElement)) {
      return;
    }
    const isMessage = node.matches?.(".p-toast-message") || node.querySelector?.(".p-toast-message");
    if (isMessage) {
      this.liveRegion.textContent = "";
    }
  }

  ngOnDestroy(): void {
    const host = this.host.nativeElement;
    host.removeEventListener("focusin", this.onFocusIn);
    host.removeEventListener("focusout", this.onFocusOut);
    this.observer?.disconnect();
    this.observer = null;
    this.liveRegion?.remove();
    this.liveRegion = null;
  }
}
